import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  setActiveGoal,
  updateActiveGoal,
  getActiveGoal,
  cancelGoal,
  formatCurrency,
  weeksUntilTarget,
} from '../../services/firebase';

// How many budget weeks remain until a given date (inclusive of the current partial week).
// Handles both Date objects and YYYY-MM-DD strings.
// Always normalizes to local midnight to avoid UTC timezone issues —
// new Date('2026-09-06') parses as UTC midnight which is Sep 5 at 8pm ET.
const weeksUntil = (dateInput) => {
  let d;
  if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const [yr, mo, dy] = dateInput.split('-').map(Number);
    d = new Date(yr, mo - 1, dy);
  } else {
    d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  }
  return weeksUntilTarget(d);
};

// Format a Date as YYYY-MM-DD for <input type="date">
// Use local date components to avoid UTC timezone shift
// (toISOString() converts to UTC which can change the date in negative-offset timezones)
const toInputDate = (date) => {
  const yr = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const dy = String(date.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy}`;
};

// Minimum target date = 2 weeks from today
const minTargetDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d;
};

const SetGoalPage = () => {
  const { family } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Edit mode — existing goal passed via navigation state
  const existingGoal = location.state?.goal || null;
  const isEditMode = !!existingGoal;

  const allowance = family?.allowance?.amount || 0;

  // In edit mode, the weekly target computes from remaining balance, not full target
  const amountAlreadySaved = isEditMode ? (existingGoal.amountSaved || 0) : 0;


  // Helper to get a JS Date from a Firestore timestamp or Date
  const toJsDate = (val) => {
    if (!val) return minTargetDate();
    if (val.toDate) return val.toDate();
    if (val.seconds) return new Date(val.seconds * 1000);
    if (val instanceof Date) return val;
    return new Date(val);
  };

  // Form state — pre-populated in edit mode
  const [goalName, setGoalName] = useState(isEditMode ? existingGoal.name : '');
  const [targetAmount, setTargetAmount] = useState(
    isEditMode ? existingGoal.targetAmount.toString() : ''
  );
  const [targetDate, setTargetDate] = useState(
    isEditMode ? toInputDate(toJsDate(existingGoal.targetDate)) : toInputDate(minTargetDate())
  );
  const [weeklyTarget, setWeeklyTarget] = useState(null); // computed
  const [weeklyOverride, setWeeklyOverride] = useState(''); // manual override
  const [userEditedWeekly, setUserEditedWeekly] = useState(false);
  const [initialAllocation, setInitialAllocation] = useState('');
  const [existingSavings, setExistingSavings] = useState(0);
  const [showInitialAlloc, setShowInitialAlloc] = useState(false);

  // Edit mode only: additional savings the student wants to apply toward the goal mid-flight
  const [editAllocation, setEditAllocation] = useState('');

  // Step: 'form' | 'plan' | 'saving'
  const [step, setStep] = useState('form');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  // Load existing savings total for retroactive allocation prompt
  useEffect(() => {
    if (!family?.id) return;
    // Sum closed weeks saved
    import('../../services/firebase').then(({ subscribeToWeeks }) => {
      const unsub = subscribeToWeeks(family.id, (weeks) => {
        const total = weeks
          .filter(w => w.closedOut)
          .reduce((s, w) => s + (w.saved || 0), 0);
        setExistingSavings(Math.round(total * 100) / 100);
      });
      return unsub;
    });
  }, [family?.id]);

  // Recompute weeklyTarget when targetAmount, targetDate, or editAllocation changes.
  // In edit mode, compute from remaining balance (target - already saved - edit alloc), not full target.
  // Always reset weeklyOverride and the user-edited flag — the goal params changed.
  useEffect(() => {
    const amount = parseFloat(targetAmount);
    if (!amount || !targetDate) { setWeeklyTarget(null); setWeeklyOverride(''); return; }
    const weeks = weeksUntil(targetDate);
    const base = isEditMode
      ? Math.max(0, amount - amountAlreadySaved - editAllocVal)
      : amount;
    const computed = base > 0
      ? Math.round(Math.ceil((base / weeks) * 100) / 100 * 100) / 100
      : 0;

    setWeeklyTarget(computed);
    setWeeklyOverride(computed.toFixed(2));
    setUserEditedWeekly(false);
  }, [targetAmount, targetDate, amountAlreadySaved, editAllocation]);

  // When initialAllocation changes and the user hasn't manually edited the weekly field,
  // recalculate and sync weeklyOverride to reflect the reduced remaining amount.
  // In edit mode, also subtract amountAlreadySaved since the student has already saved that.
  useEffect(() => {
    if (userEditedWeekly) return; // respect manual override
    const amount = parseFloat(targetAmount);
    const alloc = parseFloat(initialAllocation) || 0;
    if (!amount || !targetDate) return;
    const alreadySaved = isEditMode ? amountAlreadySaved : 0;
    const remaining = Math.max(0, amount - alreadySaved - alloc);
    const weeks = weeksUntil(targetDate);
    const computed = remaining > 0
      ? Math.round(Math.ceil((remaining / weeks) * 100) / 100 * 100) / 100
      : 0;
    setWeeklyOverride(computed.toFixed(2));
  }, [initialAllocation]);

  const targetAmountVal = parseFloat(targetAmount) || 0;
  const initialAllocVal = parseFloat(initialAllocation) || 0;
  const editAllocVal = parseFloat(editAllocation) || 0;

  // Unallocated savings = total saved - purchased goals - already saved toward active goal
  // Used in edit mode to cap the mid-goal allocation input
  const unallocatedSavings = Math.max(0, Math.round(
    (existingSavings - amountAlreadySaved) * 100
  ) / 100);

  // Max the student can apply: can't exceed unallocated savings OR remaining goal balance
  const editAllocMax = Math.min(
    unallocatedSavings,
    Math.max(0, targetAmountVal - amountAlreadySaved)
  );

  // Remaining amount after any retroactive allocation from existing savings
  // In edit mode, remaining is based on what's already saved toward the goal plus any
  // additional allocation the student is applying now via the edit form.
  // In new goal mode, remaining is reduced by any retroactive allocation the student applies.
  const remainingAmount = Math.max(0, Math.round(
    (targetAmountVal - (isEditMode ? amountAlreadySaved + editAllocVal : initialAllocVal)) * 100
  ) / 100);

  // weeklyOverride is always kept in sync via useEffect unless user manually edits it
  const effectiveWeekly = parseFloat(weeklyOverride) || weeklyTarget || 0;

  // Parse the date input string as a LOCAL date. new Date('2026-08-02') parses as
  // UTC midnight, which in US timezones is the previous evening — shifting the day of
  // week and causing the date to drift on save. Splitting the parts avoids that.
  const parseLocalDate = (str) => {
    if (str instanceof Date) return str;
    if (typeof str === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [y, m, d] = str.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(str);
  };

  const targetDateObj = parseLocalDate(targetDate);
  const weeksNeeded = effectiveWeekly > 0
    ? Math.ceil(remainingAmount / effectiveWeekly)
    : 0;
  // Use the student's chosen date as-is. We intentionally do NOT round to a Sunday or
  // otherwise mutate it on save — doing so caused the target date to drift each time
  // the goal was edited.
  const projectedDate = parseLocalDate(targetDate);

  const isOverBudget = effectiveWeekly > allowance * 0.8;

  const canProceedForm = goalName.trim().length > 0
    && targetAmountVal > 0
    && parseLocalDate(targetDate) > new Date();

  const canSave = canProceedForm && effectiveWeekly > 0 && !isOverBudget
    && (!isEditMode || editAllocVal <= editAllocMax);

  const handleDelete = async () => {
    if (!family?.id) return;
    setDeleting(true);
    setError('');
    try {
      await cancelGoal(family.id);
      navigate('/savings');
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!family?.id || !canSave) return;
    setSaving(true);
    setError('');
    try {
      if (isEditMode) {
        await updateActiveGoal(family.id, {
          name: goalName.trim(),
          targetAmount: targetAmountVal,
          targetDate: projectedDate,
          weeklyTarget: effectiveWeekly,
          ...(editAllocVal > 0 && { additionalAllocation: Math.min(editAllocVal, editAllocMax) }),
        });
      } else {
        await setActiveGoal(family.id, {
          name: goalName.trim(),
          targetAmount: targetAmountVal,
          targetDate: projectedDate,
          weeklyTarget: effectiveWeekly,
          initialAllocation: Math.min(initialAllocVal, existingSavings, targetAmountVal),
        });
      }
      navigate('/savings');
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="screen screen--no-nav">
      {/* Back */}
      <button
        onClick={() => step === 'plan' ? setStep('form') : navigate('/savings')}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--color-text-2)', marginBottom: 24, fontSize: '0.9rem',
          display: 'flex', alignItems: 'center', gap: 6, padding: 0,
          fontFamily: 'var(--font-body)',
        }}>
        ← Back
      </button>

      {/* ── Step 1: Goal details form ──────────────────────────────────── */}
      {step === 'form' && (
        <div className="animate-in">
          <h2 style={{ marginBottom: 8 }}>{isEditMode ? 'Edit your goal' : 'Set a savings goal'}</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 28, lineHeight: 1.6 }}>
            {isEditMode
              ? `You've saved ${formatCurrency(amountAlreadySaved)} so far. Your weekly target will recalculate from what's left.`
              : "Name what you're saving for and when you want it. We'll work out the weekly plan."}
          </p>

          <div className="stack stack-5">

            {/* Goal name */}
            <div className="form-group">
              <label className="form-label">What are you saving for?</label>
              <input
                className="form-input"
                type="text"
                placeholder="e.g. AirPods Pro, Spring Break, New laptop"
                value={goalName}
                onChange={e => setGoalName(e.target.value)}
                maxLength={40}
                autoFocus
              />
            </div>

            {/* Target amount */}
            <div className="form-group">
              <label className="form-label">How much does it cost?</label>
              <div className="input-with-prefix">
                <span className="input-prefix">$</span>
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="0"
                  value={targetAmount}
                  onChange={e => setTargetAmount(e.target.value)}
                  style={{ fontSize: '1.5rem', fontFamily: 'var(--font-display)', fontWeight: 700 }}
                />
              </div>
            </div>

            {/* Target date */}
            <div className="form-group">
              <label className="form-label">When do you want it by?</label>
              <input
                className="form-input"
                type="date"
                min={toInputDate(minTargetDate())}
                value={targetDate}
                onChange={e => setTargetDate(e.target.value)}
              />
            </div>

            {/* Apply additional savings — edit mode only, shown when unallocated savings exist */}
            {isEditMode && unallocatedSavings > 0 && targetAmountVal > amountAlreadySaved && (
              <div className="card animate-in" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '13px 16px 11px', display: 'flex',
                  justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--color-primary)',
                      fontSize: '0.875rem', marginBottom: 3 }}>
                      Apply savings toward this goal
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)',
                      lineHeight: 1.4 }}>
                      Put some of your unallocated savings toward this goal to reach it faster.
                    </div>
                  </div>
                  <div style={{ background: 'var(--color-primary-pale)',
                    border: '1px solid var(--color-primary-light)',
                    borderRadius: 'var(--radius-full)', padding: '3px 10px',
                    fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 600,
                    whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {formatCurrency(unallocatedSavings)} free
                  </div>
                </div>
                <div style={{ borderTop: '1px solid var(--color-border)',
                  padding: '13px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <div className="input-with-prefix" style={{ flex: 1 }}>
                      <span className="input-prefix">$</span>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        max={editAllocMax}
                        step="1"
                        placeholder="0"
                        value={editAllocation}
                        onChange={e => setEditAllocation(e.target.value)}
                        style={{ fontSize: '1.25rem', fontFamily: 'var(--font-display)',
                          fontWeight: 700 }}
                      />
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)',
                      flexShrink: 0 }}>
                      of {formatCurrency(targetAmountVal - amountAlreadySaved)} left
                    </div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)' }}>
                    Max: {formatCurrency(editAllocMax)}
                    {unallocatedSavings < (targetAmountVal - amountAlreadySaved)
                      ? ' (your unallocated savings)'
                      : ' (remaining goal balance)'}
                  </div>
                  {editAllocVal > editAllocMax && (
                    <div className="form-error" style={{ marginTop: 6 }}>
                      Can't exceed {formatCurrency(editAllocMax)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Weekly preview — show once both amount and date are filled */}
            {weeklyTarget !== null && targetAmountVal > 0 && (
              <div className="card--accent animate-in">
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--color-text-2)' }}>
                    Weekly savings needed
                  </span>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem',
                    fontWeight: 700, color: 'var(--color-primary)' }}>
                    {formatCurrency(weeklyTarget)}
                  </span>
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)', lineHeight: 1.5 }}>
                  {weeksUntil(new Date(targetDate))} weeks to reach {formatCurrency(targetAmountVal)}.
                  {isEditMode && editAllocVal > 0 && (
                    <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
                      {' '}({formatCurrency(remainingAmount)} still to save)
                    </span>
                  )}
                </div>
                {isOverBudget && (
                  <div className="alert alert--error" style={{ marginTop: 10 }}>
                    That's more than 80% of your weekly allowance ({formatCurrency(allowance)}).
                    Try a later date or a smaller goal.
                  </div>
                )}
              </div>
            )}

          </div>

          <button
            className="btn btn--primary btn--full btn--lg"
            style={{ marginTop: 32 }}
            onClick={() => setStep('plan')}
            disabled={!canProceedForm || isOverBudget}>
            {isEditMode ? 'See the updated plan →' : 'See the plan →'}
          </button>

          {/* Delete goal — edit mode only, on form step for immediate access */}
          {isEditMode && (
            <div style={{ marginTop: 24, borderTop: '1px solid var(--color-border)',
              paddingTop: 20 }}>
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--color-error)', fontSize: '0.875rem', fontWeight: 600,
                    fontFamily: 'var(--font-body)', display: 'block', margin: '0 auto' }}>
                  Delete goal
                </button>
              ) : (
                <div className="alert alert--error animate-in">
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Delete this goal?</div>
                  <div style={{ fontSize: '0.8125rem', marginBottom: 12, lineHeight: 1.5 }}>
                    Your savings total won't change, but weekly allocations toward this goal
                    will be cleared from your history.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn--secondary"
                      style={{ flex: 1, fontSize: '0.875rem' }}
                      onClick={() => setConfirmDelete(false)}>
                      Keep it
                    </button>
                    <button className="btn btn--danger"
                      style={{ flex: 1, fontSize: '0.875rem' }}
                      onClick={handleDelete}
                      disabled={deleting}>
                      {deleting
                        ? <span className="spinner spinner--white"
                            style={{ width: 16, height: 16 }} />
                        : 'Yes, delete'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Step 2: Confirm plan + optional initial allocation ─────────── */}
      {step === 'plan' && (
        <div className="animate-in">
          <h2 style={{ marginBottom: 8 }}>{isEditMode ? 'Updated plan' : "Here's your plan"}</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 24, lineHeight: 1.6 }}>
            {isEditMode
              ? editAllocVal > 0
                ? `Applying ${formatCurrency(editAllocVal)} from savings. Weekly target based on ${formatCurrency(remainingAmount)} still remaining.`
                : `Weekly target is based on the ${formatCurrency(Math.max(0, targetAmountVal - amountAlreadySaved))} still remaining.`
              : 'Review the numbers and adjust if needed. You can always edit this later.'}
          </p>

          {/* Plan summary card */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="stack stack-4">
              <PlanRow label="Goal" value={goalName} />
              <PlanRow label="Target amount" value={formatCurrency(targetAmountVal)} />
              {isEditMode && amountAlreadySaved > 0 && (
                <PlanRow label="Already saved" value={formatCurrency(amountAlreadySaved)} />
              )}
              {isEditMode && editAllocVal > 0 && (
                <PlanRow label="Applying now" value={`+ ${formatCurrency(editAllocVal)}`} />
              )}
              {isEditMode && amountAlreadySaved > 0 && (
                <PlanRow label="Still to go" value={formatCurrency(remainingAmount)} />
              )}
              <PlanRow label="Target date" value={projectedDate.toLocaleDateString('en-US',
                { month: 'short', day: 'numeric', year: 'numeric' })} />
              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center' }}>
                  <span style={{ fontSize: '0.9375rem', color: 'var(--color-text-2)' }}>
                    Save each week
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="input-with-prefix" style={{ width: 110 }}>
                      <span className="input-prefix" style={{ fontSize: '0.9rem' }}>$</span>
                      <input
                        className="form-input"
                        type="number"
                        min="1"
                        step="0.01"
                        value={weeklyOverride}
                        onChange={e => { setWeeklyOverride(e.target.value); setUserEditedWeekly(true); }}
                        style={{ fontSize: '1rem', fontFamily: 'var(--font-display)',
                          fontWeight: 700, paddingLeft: 24, textAlign: 'right' }}
                      />
                    </div>
                  </div>
                </div>
                {initialAllocVal > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-primary)',
                    marginTop: 6, textAlign: 'right' }}>
                    Adjusted for {formatCurrency(initialAllocVal)} existing savings —{' '}
                    {formatCurrency(remainingAmount)} left to save
                  </div>
                )}
                {!initialAllocVal && weeklyOverride && parseFloat(weeklyOverride) !== weeklyTarget && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)',
                    marginTop: 6, textAlign: 'right' }}>
                    {parseFloat(weeklyOverride) > (weeklyTarget || 0)
                      ? `You'll hit your goal ${Math.ceil(remainingAmount / parseFloat(weeklyOverride))} weeks from now`
                      : `Adjusted target: ${projectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                  </div>
                )}
                {isOverBudget && (
                  <div className="form-error" style={{ marginTop: 6 }}>
                    Exceeds 80% of your {formatCurrency(allowance)} weekly allowance
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Retroactive allocation — only shown for new goals with prior savings */}
          {!isEditMode && existingSavings > 0 && (
            <div className="card--accent" style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: 'var(--color-primary)',
                    marginBottom: 4, fontSize: '0.9rem' }}>
                    You've already saved {formatCurrency(existingSavings)}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-2)',
                    lineHeight: 1.5 }}>
                    Want to put some of that toward this goal?
                  </div>
                </div>
                <button
                  onClick={() => setShowInitialAlloc(v => !v)}
                  style={{ background: 'none', border: '1px solid var(--color-primary-light)',
                    borderRadius: 'var(--radius-full)', padding: '4px 12px',
                    fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'var(--font-body)', flexShrink: 0 }}>
                  {showInitialAlloc ? 'No thanks' : 'Yes, apply'}
                </button>
              </div>

              {showInitialAlloc && (
                <div className="form-group animate-in" style={{ marginTop: 14 }}>
                  <label className="form-label">
                    How much of your saved {formatCurrency(existingSavings)} toward this goal?
                  </label>
                  <div className="input-with-prefix">
                    <span className="input-prefix">$</span>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      max={Math.min(existingSavings, targetAmountVal)}
                      step="1"
                      placeholder="0"
                      value={initialAllocation}
                      onChange={e => setInitialAllocation(e.target.value)}
                      style={{ fontSize: '1.25rem', fontFamily: 'var(--font-display)',
                        fontWeight: 700 }}
                    />
                  </div>
                  {initialAllocVal > 0 && (
                    <div className="form-hint">
                      Goal starts at {formatCurrency(initialAllocVal)} —{' '}
                      {formatCurrency(Math.max(0, targetAmountVal - initialAllocVal))} to go
                    </div>
                  )}
                  {initialAllocVal > existingSavings && (
                    <div className="form-error">
                      Can't exceed your saved {formatCurrency(existingSavings)}
                    </div>
                  )}
                  {initialAllocVal > targetAmountVal && (
                    <div className="form-error">
                      Can't exceed the goal amount of {formatCurrency(targetAmountVal)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="alert alert--error" style={{ marginBottom: 16 }}>{error}</div>
          )}

          <button
            className="btn btn--primary btn--full btn--lg"
            onClick={handleSave}
            disabled={!canSave || saving
              || initialAllocVal > existingSavings
              || initialAllocVal > targetAmountVal
              || editAllocVal > editAllocMax}>
            {saving
              ? <span className="spinner spinner--white" style={{ width: 20, height: 20 }} />
              : isEditMode ? `Save changes ✓` : `Start saving for ${goalName} ✓`}
          </button>


        </div>
      )}
    </div>
  );
};

const PlanRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9375rem' }}>
    <span style={{ color: 'var(--color-text-2)' }}>{label}</span>
    <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>{value}</span>
  </div>
);

export default SetGoalPage;
